import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon from "@mui/icons-material/Close";
import CalculateIcon from "@mui/icons-material/Calculate";
import PercentIcon from "@mui/icons-material/Percent";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import SavingsIcon from "@mui/icons-material/Savings";
import BalanceIcon from "@mui/icons-material/Balance";
import PrintIcon from "@mui/icons-material/Print";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Line,
} from "recharts";
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import {
  getEconomicsSummary,
  fetchInvoicesEcon,
  fetchOrdersDate,
  fetchPaymentsDate,
} from "../../helpers/api_helper";
import moment from "moment";

// NOTE: This page now uses server-side calculations via getEconomicsSummary API
// for optimal performance. Raw data is only fetched when clicking on table rows.

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

// Greek number formatting (1.234,56 €)
const formatCurrencyGR = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "0,00 €";
  return new Intl.NumberFormat("el-GR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
};

// Format percentage Greek style
const formatPercentGR = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "0,00%";
  return new Intl.NumberFormat("el-GR", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

// Stat Card Component
const StatCard = ({ icon, label, value, subValue, color, bgcolor, tooltip }) => (
  <Tooltip title={tooltip || ""} arrow placement="top">
    <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: bgcolor || "#f3f4f6", color: color || "#6b7280", width: 44, height: 44 }}>
            {icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="#6b7280" sx={{ display: "block", mb: 0.25 }}>
              {label}
            </Typography>
            <Typography variant="body1" fontWeight={700} color={color || "#111827"} noWrap>
              {value}
            </Typography>
            {subValue && (
              <Typography variant="caption" color="#9ca3af">
                {subValue}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  </Tooltip>
);

// Summary Section Component
const SummarySection = ({ title, icon, children, color }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 32, height: 32 }}>
        {icon}
      </Avatar>
      <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

const MuiEconomics = () => {
  const { t } = useTranslation();

  document.title = t("Financial Overview") + " | FlexFlow ERP";

  const [fromDate, setFromDate] = useState("");
  const [tillDate, setTillDate] = useState("");
  const [loading, setLoading] = useState(false);

  // Data states (from backend API)
  const [financials, setFinancials] = useState(null);
  const [ordersProfitsTotals, setOrdersProfitsTotals] = useState([]);
  const [allTotals, setAllTotals] = useState([]);
  const [inboundTotals, setInboundTotals] = useState([]);
  const [outboundTotals, setOutboundTotals] = useState([]);

  // Modal states
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [invoicesModalOpen, setInvoicesModalOpen] = useState(false);
  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);

  // Active tab for tables
  const [activeTab, setActiveTab] = useState(0);

  // All data fetching is now done by getEconomicsSummary backend API

  const fetchAllTotals = async () => {
    if (!fromDate || !tillDate) {
      return;
    }

    setLoading(true);
    setFinancials(null);
    setOrdersProfitsTotals([]);
    setAllTotals([]);
    setInboundTotals([]);
    setOutboundTotals([]);

    try {
      // Single API call to backend - all calculations done server-side
      const data = await getEconomicsSummary(fromDate, tillDate);

      // Set summary data from backend
      setFinancials(data.summary);

      // Set grouped totals with IDs for DataGrid
      setOrdersProfitsTotals(
        (data.ordersProfitsTotals || []).map((item) => ({
          ...item,
          id: item.refcode,
        }))
      );

      setAllTotals(
        (data.invoiceTotals || []).map((item) => ({
          ...item,
          id: item.refcode,
        }))
      );

      setInboundTotals(
        (data.inboundTotals || []).map((item) => ({
          ...item,
          id: item.refcode,
        }))
      );

      setOutboundTotals(
        (data.outboundTotals || []).map((item) => ({
          ...item,
          id: item.refcode,
        }))
      );
    } catch (err) {
      console.error("Error fetching economics data:", err);
    }

    setLoading(false);
  };

  const handleOrdersRowClick = async (refcode) => {
    // Fetch orders for this refcode from backend
    const orders = await fetchOrdersDate(fromDate, tillDate, "SHIPPED");
    const filtered = (orders || []).filter((o) => (o.refcode || "UNKNOWN") === refcode);
    setSelectedOrders(filtered);
    setOrdersModalOpen(true);
  };

  const handleInvoicesRowClick = async (refcode) => {
    const fetchedInvoices = await fetchInvoicesEcon(fromDate, tillDate, refcode);
    setSelectedInvoices(fetchedInvoices || []);
    setInvoicesModalOpen(true);
  };

  const handlePaymentsRowClick = async (refcode) => {
    // Fetch payments for this refcode from backend
    const payments = await fetchPaymentsDate(fromDate, tillDate);
    const filtered = (payments || []).filter((p) => (p.refcode || "UNKNOWN") === refcode);
    setSelectedPayments(filtered);
    setPaymentsModalOpen(true);
  };

  // Financial summary comes from backend API (financials state)
  // No client-side calculation needed - all done server-side for performance

  // Chart data - Revenue vs Expenses by Category
  const revenueVsExpensesChart = useMemo(() => {
    const categories = [];

    // Add order categories (revenue)
    ordersProfitsTotals.forEach((o) => {
      categories.push({
        name: o.refcode?.substring(0, 12) || t("Other"),
        fullName: o.refcode,
        revenue: o.totalProductsAmmount,
        expenses: 0,
      });
    });

    // Add expense categories
    allTotals.forEach((i) => {
      const existing = categories.find(c => c.name === i.description?.substring(0, 12));
      if (existing) {
        existing.expenses = i.totalPrdAmmount;
      } else {
        categories.push({
          name: i.description?.substring(0, 12) || i.refcode,
          fullName: i.description,
          revenue: 0,
          expenses: i.totalPrdAmmount,
        });
      }
    });

    return categories.sort((a, b) => (b.revenue + b.expenses) - (a.revenue + a.expenses)).slice(0, 8);
  }, [ordersProfitsTotals, allTotals, t]);

  // Cash flow chart data
  const cashFlowChartData = useMemo(() => {
    if (!financials) return [];
    return [
      { name: t("Collections"), value: financials.totalInbound || 0, fill: "#10b981" },
      { name: t("Payments"), value: financials.totalOutbound || 0, fill: "#ef4444" },
    ];
  }, [financials, t]);

  // Print Financial Summary
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t("Financial Report")} - ${fromDate} ${t("to")} ${tillDate}</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
          h2 { color: #475569; margin-top: 30px; }
          .period { color: #64748b; font-size: 14px; margin-bottom: 30px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
          .card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
          .card-title { font-size: 12px; color: #64748b; text-transform: uppercase; margin-bottom: 8px; }
          .card-value { font-size: 24px; font-weight: 700; }
          .positive { color: #16a34a; }
          .negative { color: #dc2626; }
          .neutral { color: #1e293b; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #f8fafc; font-weight: 600; color: #475569; font-size: 12px; text-transform: uppercase; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 12px; text-align: center; }
        </style>
      </head>
      <body>
        <h1>${t("Financial Report")}</h1>
        <div class="period">${t("Period")}: ${moment(fromDate).format("DD/MM/YYYY")} - ${moment(tillDate).format("DD/MM/YYYY")}</div>

        <h2>${t("Revenue & Expenses")}</h2>
        <div class="grid">
          <div class="card">
            <div class="card-title">${t("Total Revenue")} (${t("incl. VAT")})</div>
            <div class="card-value positive">${formatCurrencyGR(financials.totalRevenue)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("Total Expenses")} (${t("excl. VAT")})</div>
            <div class="card-value negative">${formatCurrencyGR(financials.totalExpenses)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("Gross Profit")}</div>
            <div class="card-value ${financials.grossProfit >= 0 ? 'positive' : 'negative'}">${formatCurrencyGR(financials.grossProfit)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("Profit Margin")}</div>
            <div class="card-value ${financials.grossProfitMargin >= 0 ? 'positive' : 'negative'}">${formatPercentGR(financials.grossProfitMargin)}</div>
          </div>
        </div>

        <h2>${t("VAT Summary")} (${t("VAT")})</h2>
        <div class="grid">
          <div class="card">
            <div class="card-title">${t("VAT Collected")} (${t("Output VAT")})</div>
            <div class="card-value neutral">${formatCurrencyGR(financials.vatCollected)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("VAT Paid")} (${t("Input VAT")})</div>
            <div class="card-value neutral">${formatCurrencyGR(financials.vatPaid)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("VAT Payable")}</div>
            <div class="card-value ${financials.vatBalance >= 0 ? 'negative' : 'positive'}">${formatCurrencyGR(financials.vatBalance)}</div>
          </div>
        </div>

        <h2>${t("Cash Flow")}</h2>
        <div class="grid">
          <div class="card">
            <div class="card-title">${t("Collections")}</div>
            <div class="card-value positive">${formatCurrencyGR(financials.totalInbound)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("Payments")}</div>
            <div class="card-value negative">${formatCurrencyGR(financials.totalOutbound)}</div>
          </div>
          <div class="card">
            <div class="card-title">${t("Net Cash Flow")}</div>
            <div class="card-value ${financials.netCashFlow >= 0 ? 'positive' : 'negative'}">${formatCurrencyGR(financials.netCashFlow)}</div>
          </div>
        </div>

        <div class="footer">
          ${t("Generated on")} ${moment().format("DD/MM/YYYY HH:mm")} | FlexFlow ERP
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const breadcrumbs = [
    { label: t("Dashboard"), path: "/dashboard" },
    { label: t("Financial Overview") },
  ];

  const hasData = financials !== null;

  return (
    <Box sx={{ py: 2, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Container maxWidth={false}>
        <MuiPageHeader
          title={t("Financial Overview")}
          subtitle={t("Revenue, expenses, VAT and cash flow analysis")}
          breadcrumbs={breadcrumbs}
          action={
            hasData ? (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                {t("Print Report")}
              </Button>
            ) : undefined
          }
        />

        {/* Date Filter Card */}
        <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", mb: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  label={t("From")}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                  type="date"
                  size="small"
                  label={t("To")}
                  value={tillDate}
                  onChange={(e) => setTillDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Stack>

              <Button
                variant="contained"
                onClick={fetchAllTotals}
                disabled={loading || !fromDate || !tillDate}
                startIcon={<CalculateIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                  bgcolor: "#3b82f6",
                  "&:hover": { bgcolor: "#2563eb" },
                }}
              >
                {t("Calculate")}
              </Button>
            </Stack>
          </CardContent>
          {loading && <LinearProgress />}
        </Card>

        {/* Loading State */}
        {loading && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="#6b7280" sx={{ mt: 2 }}>
              {t("Calculating financial data...")}
            </Typography>
          </Box>
        )}

        {/* Financial Dashboard */}
        {!loading && hasData && (
          <>
            {/* Main KPIs Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Gross Profit - Main KPI */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `2px solid ${financials.grossProfit >= 0 ? "#10b981" : "#ef4444"}`,
                    bgcolor: financials.grossProfit >= 0 ? "#ecfdf5" : "#fef2f2",
                    height: "100%"
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: financials.grossProfit >= 0 ? "#10b981" : "#ef4444",
                          color: "#fff",
                          width: 56,
                          height: 56
                        }}
                      >
                        <SavingsIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="#64748b" fontWeight={500}>
                          {t("Gross Profit")}
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          color={financials.grossProfit >= 0 ? "#16a34a" : "#dc2626"}
                        >
                          {formatCurrencyGR(financials.grossProfit)}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PercentIcon sx={{ fontSize: 14, color: "#64748b" }} />
                          <Typography variant="body2" color="#64748b">
                            {t("Margin")}: {formatPercentGR(financials.grossProfitMargin)}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* VAT Balance */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    height: "100%"
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: "#fef3c7", color: "#d97706", width: 56, height: 56 }}>
                        <BalanceIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="#64748b" fontWeight={500}>
                          {t("VAT Payable")}
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          color={financials.vatBalance >= 0 ? "#d97706" : "#16a34a"}
                        >
                          {formatCurrencyGR(Math.abs(financials.vatBalance))}
                        </Typography>
                        <Typography variant="body2" color="#64748b">
                          {financials.vatBalance >= 0 ? t("To pay") : t("To receive")}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Net Cash Flow */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `2px solid ${financials.netCashFlow >= 0 ? "#3b82f6" : "#ef4444"}`,
                    bgcolor: financials.netCashFlow >= 0 ? "#eff6ff" : "#fef2f2",
                    height: "100%"
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: financials.netCashFlow >= 0 ? "#3b82f6" : "#ef4444",
                          color: "#fff",
                          width: 56,
                          height: 56
                        }}
                      >
                        <AccountBalanceWalletIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="#64748b" fontWeight={500}>
                          {t("Net Cash Flow")}
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          color={financials.netCashFlow >= 0 ? "#2563eb" : "#dc2626"}
                        >
                          {formatCurrencyGR(financials.netCashFlow)}
                        </Typography>
                        <Typography variant="body2" color="#64748b">
                          {financials.netCashFlow >= 0 ? t("Positive flow") : t("Negative flow")}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Stats Row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Revenue */}
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  icon={<TrendingUpIcon sx={{ fontSize: 22 }} />}
                  label={t("Revenue")}
                  value={formatCurrencyGR(financials.totalRevenue)}
                  subValue={`${financials.orderCount} ${t("orders")}`}
                  color="#16a34a"
                  bgcolor="#dcfce7"
                  tooltip={t("Total revenue including VAT from shipped orders")}
                />
              </Grid>

              {/* Expenses */}
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  icon={<TrendingDownIcon sx={{ fontSize: 22 }} />}
                  label={t("Expenses")}
                  value={formatCurrencyGR(financials.totalExpenses)}
                  subValue={`${financials.invoiceCount} ${t("invoices")}`}
                  color="#dc2626"
                  bgcolor="#fee2e2"
                  tooltip={t("Total expenses excluding VAT from supplier invoices")}
                />
              </Grid>

              {/* VAT Collected */}
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  icon={<ReceiptLongIcon sx={{ fontSize: 22 }} />}
                  label={t("Output VAT")}
                  value={formatCurrencyGR(financials.vatCollected)}
                  subValue={t("From sales")}
                  color="#8b5cf6"
                  bgcolor="#f3e8ff"
                  tooltip={t("VAT collected from customer sales")}
                />
              </Grid>

              {/* VAT Paid */}
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  icon={<ReceiptIcon sx={{ fontSize: 22 }} />}
                  label={t("Input VAT")}
                  value={formatCurrencyGR(financials.vatPaid)}
                  subValue={t("From purchases")}
                  color="#0ea5e9"
                  bgcolor="#e0f2fe"
                  tooltip={t("VAT paid on supplier purchases")}
                />
              </Grid>

              {/* Collections */}
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  icon={<PaymentsIcon sx={{ fontSize: 22 }} />}
                  label={t("Collections")}
                  value={formatCurrencyGR(financials.totalInbound)}
                  subValue={`${inboundTotals.reduce((s, p) => s + (p.count || 0), 0)} ${t("payments")}`}
                  color="#10b981"
                  bgcolor="#d1fae5"
                  tooltip={t("Total cash received from customers")}
                />
              </Grid>

              {/* Payments Out */}
              <Grid item xs={6} sm={4} md={2}>
                <StatCard
                  icon={<ShoppingCartIcon sx={{ fontSize: 22 }} />}
                  label={t("Payments")}
                  value={formatCurrencyGR(financials.totalOutbound)}
                  subValue={`${outboundTotals.reduce((s, p) => s + (p.count || 0), 0)} ${t("payments")}`}
                  color="#f59e0b"
                  bgcolor="#fef3c7"
                  tooltip={t("Total cash paid to suppliers")}
                />
              </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Revenue vs Expenses Chart */}
              <Grid item xs={12} md={8}>
                <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                    <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                      {t("Revenue vs Expenses by Category")}
                    </Typography>
                  </Box>
                  <CardContent>
                    {revenueVsExpensesChart.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={revenueVsExpensesChart} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrencyGR(v).replace("€", "").trim()} />
                          <RechartsTooltip
                            contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                            formatter={(value, name) => [formatCurrencyGR(value), name === "revenue" ? t("Revenue") : t("Expenses")]}
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ""}
                          />
                          <Legend formatter={(value) => value === "revenue" ? t("Revenue") : t("Expenses")} />
                          <Bar dataKey="revenue" name="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="expenses" name="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="#6b7280">{t("No data available")}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Cash Flow Pie Chart */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                    <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                      {t("Cash Flow Distribution")}
                    </Typography>
                  </Box>
                  <CardContent>
                    {(inboundTotals.length > 0 || outboundTotals.length > 0) ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={cashFlowChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {cashFlowChartData.map((entry, index) => (
                              <Cell key={index} fill={entry.fill} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value) => formatCurrencyGR(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="#6b7280">{t("No payment data")}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Data Tables */}
            <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Box sx={{ borderBottom: "1px solid #e2e8f0" }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, v) => setActiveTab(v)}
                  sx={{
                    px: 2,
                    "& .MuiTab-root": { textTransform: "none", fontWeight: 500 }
                  }}
                >
                  <Tab
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TrendingUpIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        <span>{t("Sales")} ({ordersProfitsTotals.length})</span>
                      </Stack>
                    }
                  />
                  <Tab
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TrendingDownIcon sx={{ fontSize: 18, color: "#ef4444" }} />
                        <span>{t("Expenses")} ({allTotals.length})</span>
                      </Stack>
                    }
                  />
                  <Tab
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PaymentsIcon sx={{ fontSize: 18, color: "#10b981" }} />
                        <span>{t("Collections")} ({inboundTotals.length})</span>
                      </Stack>
                    }
                  />
                  <Tab
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ShoppingCartIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                        <span>{t("Payments")} ({outboundTotals.length})</span>
                      </Stack>
                    }
                  />
                </Tabs>
              </Box>

              <Box sx={{ height: 400 }}>
                {activeTab === 0 && (
                  <DataGrid
                    rows={ordersProfitsTotals}
                    columns={[
                      { field: "refcode", headerName: t("Category"), flex: 1 },
                      { field: "count", headerName: t("Count"), width: 80, align: "center" },
                      {
                        field: "totalProductsAmmount",
                        headerName: t("Net Amount"),
                        flex: 1,
                        renderCell: (params) => (
                          <Typography fontWeight={500}>{formatCurrencyGR(params.value)}</Typography>
                        ),
                      },
                      {
                        field: "totalVat",
                        headerName: t("VAT"),
                        width: 120,
                        renderCell: (params) => formatCurrencyGR(params.value),
                      },
                      {
                        field: "totalOrder",
                        headerName: t("Total"),
                        flex: 1,
                        renderCell: (params) => (
                          <Typography color="#16a34a" fontWeight={600}>
                            {formatCurrencyGR(params.value)}
                          </Typography>
                        ),
                      },
                    ]}
                    onRowClick={(params) => handleOrdersRowClick(params.row.refcode)}
                    disableRowSelectionOnClick
                    sx={dataGridStyles}
                  />
                )}

                {activeTab === 1 && (
                  <DataGrid
                    rows={allTotals}
                    columns={[
                      { field: "refcode", headerName: t("Code"), width: 100 },
                      { field: "description", headerName: t("Category"), flex: 1 },
                      { field: "count", headerName: t("Count"), width: 80, align: "center" },
                      {
                        field: "totalPrdAmmount",
                        headerName: t("Amount"),
                        flex: 1,
                        renderCell: (params) => (
                          <Typography color="#dc2626" fontWeight={600}>
                            {formatCurrencyGR(params.value)}
                          </Typography>
                        ),
                      },
                      {
                        field: "totalVat",
                        headerName: t("VAT"),
                        width: 120,
                        renderCell: (params) => formatCurrencyGR(params.value),
                      },
                    ]}
                    onRowClick={(params) => handleInvoicesRowClick(params.row.refcode)}
                    disableRowSelectionOnClick
                    sx={dataGridStyles}
                  />
                )}

                {activeTab === 2 && (
                  <DataGrid
                    rows={inboundTotals}
                    columns={[
                      { field: "refcode", headerName: t("Category"), flex: 1 },
                      { field: "type", headerName: t("Type"), width: 120 },
                      { field: "count", headerName: t("Count"), width: 80, align: "center" },
                      {
                        field: "totalAmmount",
                        headerName: t("Amount"),
                        flex: 1,
                        renderCell: (params) => (
                          <Typography color="#16a34a" fontWeight={600}>
                            {formatCurrencyGR(params.value)}
                          </Typography>
                        ),
                      },
                    ]}
                    onRowClick={(params) => handlePaymentsRowClick(params.row.refcode)}
                    disableRowSelectionOnClick
                    sx={dataGridStyles}
                  />
                )}

                {activeTab === 3 && (
                  <DataGrid
                    rows={outboundTotals}
                    columns={[
                      { field: "refcode", headerName: t("Category"), flex: 1 },
                      { field: "type", headerName: t("Type"), width: 120 },
                      { field: "count", headerName: t("Count"), width: 80, align: "center" },
                      {
                        field: "totalAmmount",
                        headerName: t("Amount"),
                        flex: 1,
                        renderCell: (params) => (
                          <Typography color="#f59e0b" fontWeight={600}>
                            {formatCurrencyGR(params.value)}
                          </Typography>
                        ),
                      },
                    ]}
                    onRowClick={(params) => handlePaymentsRowClick(params.row.refcode)}
                    disableRowSelectionOnClick
                    sx={dataGridStyles}
                  />
                )}
              </Box>

              {/* Table Footer with Totals */}
              <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                <Stack direction="row" justifyContent="flex-end" spacing={4}>
                  {activeTab === 0 && (
                    <>
                      <Typography variant="body2" color="#64748b">
                        {t("Total Net")}: <strong>{formatCurrencyGR(financials.revenueNet)}</strong>
                      </Typography>
                      <Typography variant="body2" color="#64748b">
                        {t("Total VAT")}: <strong>{formatCurrencyGR(financials.vatCollected)}</strong>
                      </Typography>
                      <Typography variant="body2" color="#16a34a" fontWeight={600}>
                        {t("Grand Total")}: {formatCurrencyGR(financials.totalRevenue)}
                      </Typography>
                    </>
                  )}
                  {activeTab === 1 && (
                    <>
                      <Typography variant="body2" color="#64748b">
                        {t("Total VAT")}: <strong>{formatCurrencyGR(financials.vatPaid)}</strong>
                      </Typography>
                      <Typography variant="body2" color="#dc2626" fontWeight={600}>
                        {t("Grand Total")}: {formatCurrencyGR(financials.totalExpenses)}
                      </Typography>
                    </>
                  )}
                  {activeTab === 2 && (
                    <Typography variant="body2" color="#16a34a" fontWeight={600}>
                      {t("Total Collections")}: {formatCurrencyGR(financials.totalInbound)}
                    </Typography>
                  )}
                  {activeTab === 3 && (
                    <Typography variant="body2" color="#f59e0b" fontWeight={600}>
                      {t("Total Payments")}: {formatCurrencyGR(financials.totalOutbound)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Card>
          </>
        )}

        {/* Empty State */}
        {!loading && !hasData && (
          <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", p: 6, textAlign: "center" }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: "#f3f4f6", mx: "auto", mb: 2 }}>
              <CalculateIcon sx={{ fontSize: 32, color: "#9ca3af" }} />
            </Avatar>
            <Typography variant="h6" color="#374151" gutterBottom>
              {t("No financial data")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Select a date range and click Calculate to analyze your financial data")}
            </Typography>
          </Card>
        )}
      </Container>

      {/* Orders/Sales Modal */}
      <Dialog open={ordersModalOpen} onClose={() => setOrdersModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={600}>
            {t("Sales Details")}
          </Typography>
          <IconButton onClick={() => setOrdersModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={selectedOrders.map((o, i) => ({ ...o, id: o.id || i }))}
              columns={[
                { field: "id", headerName: t("ID"), width: 80 },
                { field: "updated", headerName: t("Date"), width: 120 },
                { field: "name", headerName: t("Customer"), flex: 1 },
                {
                  field: "netAmount",
                  headerName: t("Net"),
                  width: 120,
                  valueGetter: (params) => {
                    try {
                      return JSON.parse(params.row.totals).totalProductsAmmount;
                    } catch {
                      return 0;
                    }
                  },
                  renderCell: (params) => formatCurrencyGR(params.value),
                },
                {
                  field: "vat",
                  headerName: t("VAT"),
                  width: 100,
                  valueGetter: (params) => {
                    try {
                      return JSON.parse(params.row.totals).totalVat;
                    } catch {
                      return 0;
                    }
                  },
                  renderCell: (params) => formatCurrencyGR(params.value),
                },
                {
                  field: "total",
                  headerName: t("Total"),
                  width: 120,
                  valueGetter: (params) => {
                    try {
                      return JSON.parse(params.row.totals).totalOrder;
                    } catch {
                      return 0;
                    }
                  },
                  renderCell: (params) => (
                    <Typography color="#16a34a" fontWeight={600}>
                      {formatCurrencyGR(params.value)}
                    </Typography>
                  ),
                },
              ]}
              disableRowSelectionOnClick
              sx={dataGridStyles}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Invoices/Expenses Modal */}
      <Dialog open={invoicesModalOpen} onClose={() => setInvoicesModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={600}>
            {t("Expense Details")}
          </Typography>
          <IconButton onClick={() => setInvoicesModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={selectedInvoices.map((inv, i) => ({ ...inv, id: inv.id || i }))}
              columns={[
                { field: "id", headerName: t("ID"), width: 80 },
                { field: "number", headerName: t("Number"), width: 120 },
                { field: "providername", headerName: t("Supplier"), flex: 1 },
                { field: "importDate", headerName: t("Date"), width: 120 },
                {
                  field: "amount",
                  headerName: t("Amount"),
                  width: 120,
                  valueGetter: (params) => {
                    try {
                      return JSON.parse(params.row.totals).totalPrdAmmount;
                    } catch {
                      return 0;
                    }
                  },
                  renderCell: (params) => (
                    <Typography color="#dc2626" fontWeight={600}>
                      {formatCurrencyGR(params.value)}
                    </Typography>
                  ),
                },
                { field: "refcode", headerName: t("Code"), width: 100 },
              ]}
              disableRowSelectionOnClick
              sx={dataGridStyles}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Payments Modal */}
      <Dialog open={paymentsModalOpen} onClose={() => setPaymentsModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={600}>
            {t("Payment Details")}
          </Typography>
          <IconButton onClick={() => setPaymentsModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={selectedPayments.map((p, i) => ({ ...p, id: p.id || i }))}
              columns={[
                { field: "id", headerName: t("ID"), width: 80 },
                { field: "date", headerName: t("Date"), width: 120 },
                { field: "type", headerName: t("Type"), width: 100 },
                { field: "entity_name", headerName: t("Entity"), flex: 1 },
                {
                  field: "ammount",
                  headerName: t("Amount"),
                  width: 120,
                  renderCell: (params) => {
                    const isInbound = params.row.bound === "Inbound";
                    return (
                      <Typography color={isInbound ? "#16a34a" : "#f59e0b"} fontWeight={600}>
                        {formatCurrencyGR(parseFloat(params.value))}
                      </Typography>
                    );
                  },
                },
                { field: "bankname", headerName: t("Bank"), width: 120 },
                { field: "refcode", headerName: t("Category"), width: 100 },
              ]}
              disableRowSelectionOnClick
              sx={dataGridStyles}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const dataGridStyles = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#64748b",
    textTransform: "uppercase",
  },
  "& .MuiDataGrid-row": { cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } },
  "& .MuiDataGrid-cell": { borderBottom: "1px solid #f1f5f9" },
};

export default MuiEconomics;
